/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect, useContext } from 'react';
import { PropTypes } from 'prop-types';
import { PostInfoContext } from '../../../../utils/PostInfoProvider';


const MediaTitle = ({
  activeLanguage, handlerSetComponents, components, languages, id, reusableComponent, reusableComponentSlugname,
}) => {
  const initialState = {
    type: 'MediaTitle',
    primaryText: {
      es: '',
      en: '',
      ca: '',
      pt: '',
    },
    secondaryText: {
      es: '',
      en: '',
      ca: '',
      pt: '',
    },
    category: '',
    internalLink: '',
    // Extra caret lines. The three fields above are the original ones and stay
    // exactly as they are, so every already-saved block keeps working.
    extraSecondaryTexts: [],
  };

  const [index, setIndex] = useState();
  const [state, setState] = useState(initialState);

  const context = useContext(PostInfoContext);

  const inputHandler = (e) => {
    const { value, name, lang } = e.target;

    const { component } = components[index];

    let comp = {};

    if (name === 'internalLink') {
      comp = {
        ...component,
        [name]: value,
      };
    } else {
      comp = {
        ...component,
        [name]: {
          ...component[name],
          [lang.toLowerCase()]: value,
        },
      };
    }

    setState(comp);

    const componentObj = {
      id,
      component: comp,
    };

    handlerSetComponents(componentObj, index);
  };

  // Same write path as inputHandler, for the extra caret list.
  const commit = (extraSecondaryTexts) => {
    const { component } = components[index];
    const comp = { ...component, extraSecondaryTexts };

    setState(comp);
    handlerSetComponents({ id, component: comp }, index);
  };

  const currentExtras = () => (Array.isArray(state.extraSecondaryTexts) ? state.extraSecondaryTexts : []);

  const extraTextChange = (itemIndex, lang, value) => {
    commit(currentExtras().map((extra, i) => (
      i === itemIndex
        ? { ...extra, text: { ...extra.text, [lang.toLowerCase()]: value } }
        : extra
    )));
  };

  const extraLinkChange = (itemIndex, value) => {
    commit(currentExtras().map((extra, i) => (
      i === itemIndex ? { ...extra, internalLink: value } : extra
    )));
  };

  const addExtra = () => {
    commit([...currentExtras(), {
      text: {
        es: '', en: '', ca: '', pt: '',
      },
      internalLink: '',
    }]);
  };

  const removeExtra = (itemIndex) => {
    commit(currentExtras().filter((extra, i) => i !== itemIndex));
  };

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);

    const { component } = components[componentIndex];
    if (reusableComponent) {
      setState(reusableComponent);
      if (!component) handlerSetComponents({ component: { key: reusableComponentSlugname } }, componentIndex);
    } else {
      setIndex(componentIndex);
      if (component) {
        const comp = {
          ...component,
          category: context ? context.postData.postCategory[0] : 'transparent',
        };
        setState(comp);
      } else {
        const comp = {
          ...state,
          category: context ? context.postData.postCategory[0] : 'transparent',
        };
        setState(comp);
        const componentObj = {
          id,
          component: comp,
        };
        handlerSetComponents(componentObj, componentIndex);
      }
    }
  }, [components]);

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    const { component } = components[componentIndex];
    const comp = {
      ...component,
      category: context.postData.postCategory[0],
    };
    const componentObj = {
      id,
      component: comp,
    };
    setState(comp);
    handlerSetComponents(componentObj, componentIndex);
  }, [context.postData.postCategory[0]]);

  return (
    <div className="w-full px-4 bg-greyFilter rounded-lg pl-10 pr-2 flex flex-col">
    <div className="w-full flex flex-row justify-between">
      {languages.map(lang => (
        <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'block w-full' : 'hidden'}`}>
          <div className="pl-4 pt-0 bg-greyFilter flex flex-row">
            <label className="mt-1 flex flex-col font-americaMonoRegular text-xs uppercase text-black w-full" htmlFor="primaryText">
              Primary text
              <input className="mt-1 font-americaMonoRegular p-2 bg-white outline-none w-full rounded-full" onChange={inputHandler} name="primaryText" lang={lang} type="text" value={state.primaryText[lang.toLowerCase()] || ''} readOnly={reusableComponent && reusableComponent} />
            </label>
            <label className="mt-1 mx-3 flex flex-col font-americaMonoRegular text-xs uppercase text-black w-full pb-6px" htmlFor="secondaryText">
              Secondary text
              <input className="mt-1 font-americaMonoRegular p-2 bg-white outline-none w-full rounded-full" onChange={inputHandler} name="secondaryText" lang={lang} type="text" value={(state && state.hasOwnProperty('secondaryText') && state.secondaryText[lang.toLowerCase()]) || ''} readOnly={reusableComponent && reusableComponent} />
            </label>
          </div>
        </div>
      ))}
      <label className="mt-1 flex flex-col font-americaMonoRegular text-xs uppercase text-black w-47% mr-8px" htmlFor="secondaryText">
        Secondary text link (if exists)
        <input className="mt-1 font-americaMonoRegular p-2 bg-white outline-none w-full rounded-full" onChange={inputHandler} name="internalLink" type="text" value={state.internalLink || ''} readOnly={reusableComponent && reusableComponent} />
      </label>
    </div>

    {/* Extra caret lines, each rendered on the front as "› text". Optional:
        a block with none of these looks and behaves exactly as before. */}
    <div className="w-full pb-3">
      {currentExtras().map((extra, itemIndex) => (
        <div key={`extraSecondaryText_${itemIndex}`} className="w-full flex flex-row items-end pt-2">
          {languages.map(lang => (
            <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'block w-full' : 'hidden'}`}>
              <label className="mt-1 flex flex-col font-americaMonoRegular text-xs uppercase text-black w-full">
                {`Extra caret ${itemIndex + 1}`}
                <input
                  className="mt-1 font-americaMonoRegular p-2 bg-white outline-none w-full rounded-full"
                  type="text"
                  value={(extra.text && extra.text[lang.toLowerCase()]) || ''}
                  onChange={e => extraTextChange(itemIndex, lang, e.target.value)}
                  readOnly={reusableComponent && reusableComponent}
                />
              </label>
            </div>
          ))}
          <label className="mt-1 mx-3 flex flex-col font-americaMonoRegular text-xs uppercase text-black w-full">
            Link (if exists)
            <input
              className="mt-1 font-americaMonoRegular p-2 bg-white outline-none w-full rounded-full"
              type="text"
              value={extra.internalLink || ''}
              onChange={e => extraLinkChange(itemIndex, e.target.value)}
              readOnly={reusableComponent && reusableComponent}
            />
          </label>
          {!reusableComponent && (
            <div className="cursor-pointer p-2" onClick={() => removeExtra(itemIndex)}>
              <span className="font-icon icon-close-btn1" />
            </div>
          )}
        </div>
      ))}
      {!reusableComponent && (
        <div
          className="mt-2 cursor-pointer font-americaMonoRegular text-xs uppercase text-black text-center p-2 bg-white rounded-full w-full"
          onClick={addExtra}
        >
          + Add caret
        </div>
      )}
    </div>
    </div>
  );
};

MediaTitle.propTypes = {
  activeLanguage: PropTypes.string.isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  id: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  reusableComponent: PropTypes.shape(),
};
MediaTitle.defaultProps = {
  reusableComponent: null,
};

export default MediaTitle;
